import { recordActivity } from "@bodhistreams/api/src/activityApi";
import { create } from "zustand";

/**
 * activityStore — fire-and-forget user-activity tracking.
 *
 * Responsibilities:
 *   - own a per-browser-session ULID (`session_id`) kept in sessionStorage
 *     so a refresh / new tab starts a fresh session
 *   - send `session_start` exactly once per session via `markSessionStart()`
 *   - dedupe rapid repeats (same kind + entity within DEDUPE_MS)
 *   - silently no-op when called for an unauthenticated context — the
 *     <ActivityTracker> mounts behind ProtectedUserRoute so by the time
 *     `markSessionStart` runs we know the user is logged in. `track()`
 *     before that point is dropped on purpose: we don't want anonymous
 *     navigation noise.
 *
 * Network failures are intentionally swallowed: activity logs are best-effort.
 */

const SESSION_KEY = "bs:activity:session_id";
const DEDUPE_MS = 60_000;

const generateSessionId = () => {
	// Light ULID-ish: timestamp + random. Avoids pulling a ULID dependency
	// just for an opaque identifier.
	const ts = Date.now().toString(36);
	const rand = Math.random().toString(36).slice(2, 12);
	return `${ts}${rand}`;
};

const readSessionId = () => {
	if (typeof window === "undefined") return null;
	try {
		let id = window.sessionStorage.getItem(SESSION_KEY);
		if (!id) {
			id = generateSessionId();
			window.sessionStorage.setItem(SESSION_KEY, id);
		}
		return id;
	} catch {
		// sessionStorage disabled (private mode, etc.) — fall back to in-memory.
		return generateSessionId();
	}
};

const dedupeKey = (kind, entityType, entityId) =>
	`${kind}|${entityType || ""}|${entityId || ""}`;

const useActivityStore = create((set, get) => ({
	sessionId: null,
	sessionStartSent: false,
	lastSentAt: new Map(),

	/**
	 * Initialise the session_id (idempotent) and emit `session_start` exactly
	 * once per browser session. Safe to call multiple times.
	 *
	 * @param {Object} [opts]
	 * @param {Object} [opts.metadata] e.g. { user_id, membership_name }
	 */
	markSessionStart: ({ metadata } = {}) => {
		const id = get().sessionId ?? readSessionId();
		set({ sessionId: id });

		if (get().sessionStartSent) return;
		set({ sessionStartSent: true });

		recordActivity({
			kind: "session_start",
			session_id: id,
			path:
				typeof window !== "undefined" ? window.location.pathname : undefined,
			metadata,
		}).catch(() => {});
	},

	/**
	 * Record an arbitrary activity event. No-ops if `markSessionStart` hasn't
	 * been called yet (i.e. the user is not authenticated in this browser
	 * session).
	 *
	 * @param {Object} event
	 * @param {string} event.kind
	 * @param {string} [event.entityType]
	 * @param {string} [event.entityId]
	 * @param {Object} [event.metadata]
	 */
	track: ({ kind, entityType, entityId, metadata } = {}) => {
		if (!kind) return;
		const { sessionId, sessionStartSent, lastSentAt } = get();
		if (!sessionId || !sessionStartSent) return;

		const key = dedupeKey(kind, entityType, entityId);
		const now = Date.now();
		const last = lastSentAt.get(key);
		if (last && now - last < DEDUPE_MS) return;

		const nextMap = new Map(lastSentAt);
		nextMap.set(key, now);
		set({ lastSentAt: nextMap });

		recordActivity({
			kind,
			session_id: sessionId,
			entity_type: entityType,
			entity_id: entityId,
			path:
				typeof window !== "undefined" ? window.location.pathname : undefined,
			metadata,
		}).catch(() => {});
	},

	/**
	 * Reset session state after logout. Next login starts a brand-new session.
	 */
	resetSession: () => {
		try {
			if (typeof window !== "undefined") {
				window.sessionStorage.removeItem(SESSION_KEY);
			}
		} catch {
			// ignore
		}
		set({
			sessionId: null,
			sessionStartSent: false,
			lastSentAt: new Map(),
		});
	},
}));

export default useActivityStore;
